/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useContext, useEffect, useState } from 'react';
import { ContentContext } from './context';
import { isWltpComplianceSatisfied } from './wltpCompliance';
import type { Logger } from '@feature-hub/core';

export interface UseWltpComplianceGateArgs {
  isMarketComplianceEnabled?: boolean;
  initialCompliance?: boolean;
  logger?: Pick<Logger, 'warn' | 'error'>;
}

export const useWltpComplianceGate = ({
  isMarketComplianceEnabled,
  initialCompliance,
  logger,
}: UseWltpComplianceGateArgs): boolean => {
  const { content, localeService, vueFormatterService } = useContext(ContentContext);

  const wltpKeys = content?.legalText?.wltpKey as string[] | undefined;
  const hasWltpKeys = Array.isArray(wltpKeys) && wltpKeys.length > 0;
  const isGateActive = hasWltpKeys && Boolean(isMarketComplianceEnabled);
  const isServerResultAvailable = initialCompliance !== undefined;

  const [clientCompliance, setClientCompliance] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const isClientCheckNeeded = isGateActive && !isServerResultAvailable;
    if (!isClientCheckNeeded) return undefined;
    if (!content) return undefined;

    const controller = new AbortController();
    const runCheck = async () => {
      const isCompliant = await isWltpComplianceSatisfied(
        wltpKeys,
        vueFormatterService,
        localeService,
        logger,
      );
      if (!controller.signal.aborted) {
        setClientCompliance(isCompliant);
      }
    };
    void runCheck();
    return () => {
      controller.abort();
    };
  }, [
    isGateActive,
    isServerResultAvailable,
    wltpKeys,
    content,
    localeService,
    vueFormatterService,
    logger,
  ]);

  if (!isGateActive) return true;

  const isCompliant = isServerResultAvailable ? initialCompliance : clientCompliance;
  return Boolean(isCompliant);
};
